import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CircularProgress } from '@mui/material'
import Select, { SingleValue, StylesConfig } from 'react-select'

import GetStartedLayout from './GetStartedLayout'
import { OwnerRegistration } from '../../types/onboardingRegistration'
import {
  isProfessionalEmail,
  isValidEmailFormat,
} from '../../utils/professionalEmail'
import {
  evaluatePasswordRules,
  isPasswordPolicyMet,
  passwordStrengthScore,
} from '../../utils/passwordPolicy'
import { saveRegistration } from '../../utils/onboardingStorage'
import { registerOwnerApi } from '../../api/onboardingApi'
import { CountryOption, getCountryOptions } from '../../utils/countries'

const emptyForm = (): OwnerRegistration => ({
  companyName: '',
  companyAddress: '',
  firstName: '',
  lastName: '',
  username: '',
  password: '',
  phone: '',
  jobTitle: '',
  need: '',
  country: '',
  professionalEmail: '',
})

type Stage = 0 | 1 | 2

const STAGE_FIELDS: (keyof OwnerRegistration)[][] = [
  ['companyName', 'country', 'companyAddress'],
  ['firstName', 'lastName', 'username', 'password', 'professionalEmail'],
  ['phone', 'jobTitle', 'need'],
]

/** Compact select styles for get-started (no global-zoom menu compensation). */
const getCountrySelectStyles = (
  isError: boolean
): StylesConfig<CountryOption, false> => ({
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    height: 40,
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: isError
      ? '#d14a3e'
      : state.isFocused
        ? '#0d9b87'
        : '#dee4f0',
    backgroundColor: isError
      ? '#fff8f6'
      : state.isFocused
        ? '#fff'
        : '#f7f9fd',
    boxShadow: state.isFocused
      ? isError
        ? '0 0 0 3px rgba(209, 74, 62, 0.12)'
        : '0 0 0 3px rgba(13, 155, 135, 0.14)'
      : 'none',
    fontFamily: 'Lato, sans-serif',
    fontSize: 13.5,
    cursor: 'pointer',
    '&:hover': {
      borderColor: isError
        ? '#d14a3e'
        : state.isFocused
          ? '#0d9b87'
          : '#8b99b4',
      backgroundColor: isError ? '#fff8f6' : '#fff',
    },
  }),
  valueContainer: (base) => ({ ...base, padding: '2px 10px', height: 38 }),
  placeholder: (base) => ({
    ...base,
    color: '#8b99b4',
    fontSize: 13.5,
    margin: 0,
  }),
  singleValue: (base) => ({
    ...base,
    color: '#0b1e2d',
    fontSize: 13.5,
    margin: 0,
  }),
  input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: 13.5 }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: '0 10px',
    color: '#5b6b87',
  }),
  clearIndicator: (base) => ({ ...base, padding: '0 6px', color: '#8b99b4' }),
  menu: (base) => ({
    ...base,
    borderRadius: 10,
    border: '1px solid #dee4f0',
    boxShadow: '0 16px 40px rgba(6, 21, 36, 0.14)',
    overflow: 'hidden',
    zIndex: 20,
    fontFamily: 'Lato, sans-serif',
    fontSize: 13,
  }),
  menuList: (base) => ({ ...base, maxHeight: 240, padding: 4 }),
  option: (base, state) => ({
    ...base,
    borderRadius: 7,
    margin: '1px 0',
    padding: '8px 12px',
    fontSize: 13,
    fontWeight: state.isSelected ? 600 : 500,
    backgroundColor: state.isSelected
      ? '#e2f7f2'
      : state.isFocused
        ? '#f7fafa'
        : '#fff',
    color: state.isSelected ? '#076b5c' : '#0b1e2d',
    cursor: 'pointer',
  }),
})

const OwnerInfoForm: React.FC = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()

  const [form, setForm] = useState<OwnerRegistration>(emptyForm)
  const [errors, setErrors] = useState<
    Partial<Record<keyof OwnerRegistration, string>>
  >({})
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [stage, setStage] = useState<Stage>(0)

  const countryOptions = useMemo(
    () => getCountryOptions(i18n.language),
    [i18n.language]
  )

  const selectedCountry =
    countryOptions.find((o) => o.value === form.country) || null

  const updateField = (field: keyof OwnerRegistration, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }))
    }
  }

  const filledCount = useMemo(() => {
    const keys = Object.keys(emptyForm()) as (keyof OwnerRegistration)[]
    return keys.filter((k) => String(form[k] || '').trim().length > 0).length
  }, [form])

  const progressPct = Math.round((filledCount / 11) * 100)

  const validateStage = (s: Stage): boolean => {
    const next: Partial<Record<keyof OwnerRegistration, string>> = {}
    const req = t('getStarted.errors.required')

    STAGE_FIELDS[s].forEach((key) => {
      if (key === 'password') {
        if (!form.password) {
          next.password = req
        } else if (
          !isPasswordPolicyMet(form.password, {
            username: form.username,
            email: form.professionalEmail,
          })
        ) {
          next.password = t('getStarted.errors.passwordPolicy')
        }
        return
      }
      if (key === 'professionalEmail') {
        if (!form.professionalEmail.trim()) {
          next.professionalEmail = req
        } else if (!isValidEmailFormat(form.professionalEmail)) {
          next.professionalEmail = t('getStarted.errors.invalidEmail')
        } else if (!isProfessionalEmail(form.professionalEmail)) {
          next.professionalEmail = t('getStarted.errors.personalEmail')
        }
        return
      }
      if (!String(form[key] || '').trim()) next[key] = req
    })

    setErrors(next)
    return Object.keys(next).length === 0
  }

  const goNext = () => {
    if (!validateStage(stage)) return
    setStage((s) => Math.min(2, s + 1) as Stage)
  }

  const goBack = () => setStage((s) => Math.max(0, s - 1) as Stage)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitError(null)

    for (let i = 0; i <= 2; i++) {
      if (!validateStage(i as Stage)) {
        setStage(i as Stage)
        return
      }
    }

    setLoading(true)
    try {
      await registerOwnerApi(form)
      saveRegistration({
        ...form,
        submittedAt: new Date().toISOString(),
        emailVerified: false,
      })
      navigate('/get-started/verify-email')
    } catch (err: any) {
      const raw = String(
        err?.response?.data?.message || err?.message || ''
      )
      const isEmailUnavailable =
        raw === 'REGISTRATION_EMAIL_UNAVAILABLE' ||
        /compte vérifié existe déjà|verified account already exists|email already/i.test(
          raw
        )
          setSubmitError(
        isEmailUnavailable
          ? t(
              'getStarted.errors.emailUnavailable',
              'We could not complete registration with this email. Try signing in, or use a different email address.'
            )
          : /password policy|12 characters|special character/i.test(raw)
            ? t('getStarted.errors.passwordPolicy')
            : raw ||
              t(
                'getStarted.errors.registrationFailed',
                'Registration failed. Please try again.'
              )
      )
    } finally {
      setLoading(false)
    }
  }

  const field = (
    key: keyof OwnerRegistration,
    label: string,
    options?: {
      type?: string
      multiline?: boolean
      rows?: number
      placeholder?: string
      help?: string
      span?: boolean
    }
  ) => {
    const err = errors[key]
    const common = {
      id: `gs-${key}`,
      className: `gs-wiz-inp${err ? ' is-error' : ''}`,
      value: form[key],
      onChange: (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      ) => updateField(key, e.target.value),
      placeholder: options?.placeholder,
      required: true,
    }

    return (
      <div
        className={`gs-wiz-field${
          options?.multiline || options?.span ? ' gs-wiz-field--span' : ''
        }`}
        key={key}
      >
        <label className="gs-wiz-label" htmlFor={common.id}>
          {label}
          <span className="gs-wiz-req">*</span>
        </label>
        {options?.multiline ? (
          <textarea {...common} rows={options.rows || 3} />
        ) : (
          <input {...common} type={options?.type || 'text'} />
        )}
        {err ? (
          <span className="gs-wiz-err">{err}</span>
        ) : options?.help ? (
          <span className="gs-wiz-help">{options.help}</span>
        ) : null}
      </div>
    )
  }

  const countryField = () => {
    const err = errors.country
    return (
      <div className="gs-wiz-field" key="country">
        <label className="gs-wiz-label" htmlFor="gs-country">
          {t('getStarted.ownerForm.country', 'Country / region')}
          <span className="gs-wiz-req">*</span>
        </label>
        <div className={`gs-wiz-country${err ? ' is-error' : ''}`}>
          <Select<CountryOption, false>
            inputId="gs-country"
            classNamePrefix="gs-react-select"
            options={countryOptions}
            value={selectedCountry}
            onChange={(opt: SingleValue<CountryOption>) =>
              updateField('country', opt?.value || '')
            }
            placeholder={t(
              'getStarted.ownerForm.countrySearch',
              'Search country…'
            )}
            isClearable
            isSearchable
            menuShouldBlockScroll={false}
            styles={getCountrySelectStyles(Boolean(err))}
            noOptionsMessage={() =>
              t('getStarted.ownerForm.countryNoMatch', 'No country found')
            }
            filterOption={(option, raw) => {
              const q = raw.trim().toLowerCase()
              if (!q) return true
              return option.label.toLowerCase().includes(q)
            }}
          />
        </div>
        {err ? <span className="gs-wiz-err">{err}</span> : null}
      </div>
    )
  }

  const stages = [
    {
      title: t('getStarted.ownerForm.sectionCompany', 'Company'),
      hint: t('getStarted.wiz.companyHint', 'Who you represent'),
      cardTitle: t('getStarted.wiz.companyTitle', 'Organization details'),
      cardSub: t(
        'getStarted.wiz.companySub',
        'We use this to create your company workspace and compliance context.'
      ),
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
        >
          <path
            d="M3 21h18M5 21V8l7-4 7 4v13M9 21v-6h6v6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      title: t('getStarted.ownerForm.sectionAccount', 'Account'),
      hint: t('getStarted.wiz.accountHint', 'Your login'),
      cardTitle: t('getStarted.wiz.accountTitle', 'Create your owner account'),
      cardSub: t(
        'getStarted.wiz.accountSub',
        'This person becomes the organization owner and receives the verification email.'
      ),
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
        >
          <circle cx="12" cy="8" r="3.5" />
          <path
            d="M5 20c1.5-3.5 4-5 7-5s5.5 1.5 7 5"
            strokeLinecap="round"
          />
        </svg>
      ),
    },
    {
      title: t('getStarted.ownerForm.sectionProfile', 'Profile'),
      hint: t('getStarted.wiz.profileHint', 'Almost done'),
      cardTitle: t('getStarted.wiz.profileTitle', 'How we can help'),
      cardSub: t(
        'getStarted.wiz.profileSub',
        'A few details so our team can tailor the demo and plan recommendation.'
      ),
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
        >
          <path
            d="M4 19V5M4 19h16M8 16V9M12 16V7M16 16v-4"
            strokeLinecap="round"
          />
        </svg>
      ),
    },
  ]

  const current = stages[stage]
  const pwRules = evaluatePasswordRules(form.password, {
    username: form.username,
    email: form.professionalEmail,
  })
  const pwScore = passwordStrengthScore(form.password)

  return (
    <GetStartedLayout
      step={1}
      title={t(
        'getStarted.ownerForm.title',
        'Build your organization workspace'
      )}
      subtitle={t(
        'getStarted.ownerForm.subtitle',
        'Three short steps — company, account, then how we can help. About two minutes.'
      )}
      panel={false}
    >
      {submitError ? <div className="gs-alert">{submitError}</div> : null}

      <div className="gs-wiz-track" role="tablist">
        {stages.map((s, i) => {
          const done = i < stage
          const active = i === stage
          return (
            <button
              key={s.title}
              type="button"
              role="tab"
              aria-selected={active}
              className={`gs-wiz-tab${active ? ' is-active' : ''}${
                done ? ' is-done' : ''
              }`}
              onClick={() => {
                if (i < stage) setStage(i as Stage)
                else if (i > stage) {
                  if (validateStage(stage)) setStage(i as Stage)
                }
              }}
            >
              <span className="gs-wiz-tab-n">
                {done ? (
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path
                      d="M2.5 6.2L4.8 8.5 9.5 3.5"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  i + 1
                )}
              </span>
              <span className="gs-wiz-tab-copy">
                <span className="gs-wiz-tab-title">{s.title}</span>
                <span className="gs-wiz-tab-hint">{s.hint}</span>
              </span>
            </button>
          )
        })}
      </div>

      <form onSubmit={handleSubmit}>
        <div className="gs-wiz-card" key={stage}>
          <div className="gs-wiz-card-head">
            <div className="gs-wiz-card-ico" aria-hidden>
              {current.icon}
            </div>
            <div>
              <h3>{current.cardTitle}</h3>
              <p>{current.cardSub}</p>
            </div>
          </div>

          <div className="gs-wiz-card-body">
            {stage === 0 && (
              <div className="gs-wiz-grid">
                {field(
                  'companyName',
                  t('getStarted.companyName', 'Company name'),
                  {
                    placeholder: t('getStarted.wiz.phCompany', 'Acme Security'),
                  }
                )}
                {countryField()}
                {field(
                  'companyAddress',
                  t('getStarted.ownerForm.companyAddress', 'Company address'),
                  {
                    multiline: true,
                    rows: 3,
                    placeholder: t(
                      'getStarted.wiz.phAddress',
                      'Street, city, postal code'
                    ),
                  }
                )}
              </div>
            )}

            {stage === 1 && (
              <div className="gs-wiz-grid">
                {field(
                  'firstName',
                  t('getStarted.firstName', 'First name'),
                  { placeholder: t('auth.firstNamePlaceholder', 'First name') }
                )}
                {field('lastName', t('getStarted.lastName', 'Last name'), {
                  placeholder: t('auth.lastNamePlaceholder', 'Last name'),
                })}
                {field(
                  'username',
                  t('getStarted.ownerForm.username', 'Username'),
                  {
                    placeholder: t(
                      'auth.usernamePlaceholder',
                      'you@company.com'
                    ),
                    help: t(
                      'auth.usernameHelp',
                      'Letters, numbers, dots and hyphens.'
                    ),
                  }
                )}
                <div className="gs-wiz-field" key="password">
                  <label className="gs-wiz-label" htmlFor="gs-password">
                    {t('auth.password', 'Password')}
                    <span className="gs-wiz-req">*</span>
                  </label>
                  <input
                    id="gs-password"
                    className={`gs-wiz-inp${errors.password ? ' is-error' : ''}`}
                    type="password"
                    value={form.password}
                    onChange={(e) => updateField('password', e.target.value)}
                    placeholder={t('getStarted.passwordHint')}
                    autoComplete="new-password"
                    required
                  />
                  <div
                    className={`gs-wiz-pw-meter${pwScore ? ` s${pwScore}` : ''}`}
                    aria-hidden
                  >
                    <span />
                    <span />
                    <span />
                    <span />
                  </div>
                  <div className="gs-wiz-pw-rules">
                    <span className={pwRules.len ? 'is-ok' : ''}>
                      {t('auth.pwRuleLen')}
                    </span>
                    <span
                      className={
                        pwRules.upper && pwRules.lower ? 'is-ok' : ''
                      }
                    >
                      {t('auth.pwRuleCase')}
                    </span>
                    <span className={pwRules.num ? 'is-ok' : ''}>
                      {t('auth.pwRuleNum')}
                    </span>
                    <span className={pwRules.sym ? 'is-ok' : ''}>
                      {t('auth.pwRuleSym')}
                    </span>
                  </div>
                  {errors.password ? (
                    <span className="gs-wiz-err">{errors.password}</span>
                  ) : (
                    <span className="gs-wiz-help">
                      {t('getStarted.passwordHint')}
                    </span>
                  )}
                </div>
                {field(
                  'professionalEmail',
                  t(
                    'getStarted.ownerForm.professionalEmail',
                    'Professional email'
                  ),
                  {
                    type: 'email',
                    span: true,
                    placeholder: 'islem.saoudi@company.com',
                    help: t(
                      'getStarted.ownerForm.professionalEmailHint',
                      'Use your company email address (not Gmail, Yahoo, etc.)'
                    ),
                  }
                )}
              </div>
            )}

            {stage === 2 && (
              <div className="gs-wiz-grid">
                {field(
                  'phone',
                  t('getStarted.ownerForm.phone', 'Phone number'),
                  { placeholder: '+33 6 12 34 56 78' }
                )}
                {field(
                  'jobTitle',
                  t('getStarted.ownerForm.jobTitle', 'Job title'),
                  {
                    placeholder: t(
                      'getStarted.wiz.phJob',
                      'CISO / GRC Manager'
                    ),
                  }
                )}
                {field(
                  'need',
                  t('getStarted.ownerForm.need', 'Need / challenge'),
                  {
                    multiline: true,
                    rows: 3,
                    placeholder: t(
                      'getStarted.wiz.phNeed',
                      'What are you looking to improve first?'
                    ),
                  }
                )}
              </div>
            )}
          </div>

          <div className="gs-wiz-meter">
            <div className="gs-wiz-meter-bar" aria-hidden>
              <div
                className="gs-wiz-meter-fill"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <span className="gs-wiz-meter-label">
              {t('getStarted.wiz.progress', '{{pct}}% complete', {
                pct: progressPct,
              })}
            </span>
          </div>

          <div className="gs-wiz-foot">
            {stage > 0 ? (
              <button
                type="button"
                className="gs-wiz-btn gs-wiz-btn--ghost"
                onClick={goBack}
              >
                {t('getStarted.back', 'Back')}
              </button>
            ) : (
              <span />
            )}

            {stage < 2 ? (
              <button
                type="button"
                className="gs-wiz-btn gs-wiz-btn--teal"
                onClick={goNext}
              >
                {t('getStarted.wiz.continue', 'Continue')}
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  aria-hidden
                >
                  <path d="M6 3.5L10.5 8 6 12.5" strokeLinecap="round" />
                </svg>
              </button>
            ) : (
              <button
                type="submit"
                className="gs-wiz-btn gs-wiz-btn--primary"
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={18} sx={{ color: '#fff' }} />
                ) : (
                  <>
                    {t('getStarted.ownerForm.submit', 'Submit my request')}
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      aria-hidden
                    >
                      <path
                        d="M2.5 8h11M9.5 4.5L13 8l-3.5 3.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </form>

      <p className="gs-wiz-signin">
        {t('getStarted.ownerForm.footHint', 'Already have an account?')}{' '}
        <button
          type="button"
          className="gs-link"
          onClick={() => navigate('/login')}
        >
          {t('auth.signIn', 'Sign in')}
        </button>
      </p>
    </GetStartedLayout>
  )
}

export default OwnerInfoForm
